import { isEmpty, isNil } from 'lodash';
import React, { useCallback, useContext } from 'react';
import CheckTodoIcon from '@shared/assets/icons/v2/check-todo.svg';
import { View } from '@shared/components/View';
import { MBButton } from '@shared/components/button';
import { MBHover } from '@shared/components/hover';
import { MBIconV2 } from '@shared/components/image';
import { MBEmptyListPlaceholder } from '@shared/components/list';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBScrollView } from '@shared/components/scrollview';
import { MBSearchBar } from '@shared/components/searchBar';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1, MBTextOverlineBold } from '@shared/components/typography';
import { useListWorkflowTemplates } from '@shared/scenes/workflows/hooks';
import { ColorThemeContext, Colors, grid } from '@shared/styles';
import { useChannel, useDebouncedState } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { useNavigator } from '@shared/util/navigation';
import { ChannelPermissionsContext } from '@shared/util/permissions/ChannelPermissionsContext';
import { Divider } from './divider';

interface Props {
  component: MBAPI.WorkflowTemplate_components_SubformComponent;
}

export function SubFormOptionsChildren({ component }: Props) {
  const colors = useContext(ColorThemeContext);
  const { channelId } = useContext(ChannelPermissionsContext);
  const channel = useChannel({ channelId });
  const navigator = useNavigator();
  const [query, setQuery] = useDebouncedState('', 300);
  const { workflowTemplates, loading, hasMore, loadMore } = useListWorkflowTemplates({
    skip: isEmpty(component.workflowTemplateIds),
    input: { ids: component.workflowTemplateIds, name: isEmpty(query) ? undefined : query },
  });

  const channelSlug = channel?.slug;
  const workflowTemplateId = channel?.workflowTemplateId;

  const onDeletePress = useCallback(() => {
    if (isNil(channelSlug)) {
      return;
    }
    navigator.navigate('SubformsList', { channelSlug });
  }, [channelSlug, navigator]);

  const onRowPress = useCallback(
    (subformWorkflowTemplateId: string, subformComponentId: string) => {
      if (isNil(channelSlug) || isNil(workflowTemplateId)) {
        return;
      }
      navigator.navigate('WorkflowEdit', {
        channelSlug,
        workflowId: subformWorkflowTemplateId,
        queryString: {
          originWorkflowTemplateId: workflowTemplateId,
          originComponentId: subformComponentId,
        },
      });
    },
    [navigator, channelSlug, workflowTemplateId],
  );

  const onAdd = useCallback(() => {
    if (isNil(channelSlug)) {
      return;
    }
    navigator.navigate('SubformsList', { channelSlug });
  }, [channelSlug, navigator]);

  return (
    <>
      <View paddingHorizontal={3} paddingVertical={1}>
        <MBTextOverlineBold color={colors.foregroundInactive}>
          {i18n('workflowTemplate.subformSet')}
        </MBTextOverlineBold>
      </View>
      <View paddingVertical={1} paddingHorizontal={3}>
        <MBSearchBar
          value={query}
          onChangeText={setQuery}
          placeholder={i18n('workflowTemplate.subformsSearch')}
        />
      </View>
      <View style={{ maxHeight: 210 }}>
        <MBScrollView
          nativeShouldPersistTaps
          nativeKeyboardDismissMode="on-drag"
          style={{ flexGrow: 0 }}
        >
          {!loading && isEmpty(workflowTemplates) && (
            <MBEmptyListPlaceholder
              title={i18n('workflowTemplate.subformsNoMatchTitle')}
              description={i18n('workflowTemplate.subformsNoMatchDescription')}
              image={CheckTodoIcon}
              imageSize={grid(9)}
            />
          )}
          {loading && (
            <View center style={{ height: 150 }}>
              <MBLoadingIndicator />
            </View>
          )}
          {workflowTemplates?.map(wft => (
            <SubFormOption
              key={wft.id}
              subformWorkflowTemplate={wft}
              subformComponentId={component.id}
              onRowPress={onRowPress}
              onDeletePress={onDeletePress}
            />
          ))}
          {hasMore && (
            <View paddingVertical={3}>
              <MBButton
                title={i18n('card.list.showMore')}
                size="small"
                theme="transparent"
                bold={false}
                borderColor={Colors.v2.gray10}
                onPress={loadMore}
                style={{ alignSelf: 'center' }}
              />
            </View>
          )}
        </MBScrollView>
      </View>
      <AddSubformRow onAdd={onAdd} />
      <View paddingHorizontal={2} paddingTop={1}>
        <Divider />
      </View>
    </>
  );
}

interface SubFormOptionProps {
  subformWorkflowTemplate: MBAPI.WorkflowTemplate;
  subformComponentId: string;
  onRowPress(subformWorkflowTemplateId: string, subformComponentId: string): void;
  onDeletePress(): void;
}

function SubFormOption({
  subformWorkflowTemplate,
  subformComponentId,
  onRowPress,
  onDeletePress,
}: SubFormOptionProps) {
  const colors = useContext(ColorThemeContext);

  return (
    <MBTouchableOpacity onPress={() => onRowPress(subformWorkflowTemplate.id, subformComponentId)}>
      <MBHover hoverStyle={{ backgroundColor: colors.input.backgroundInactive }} centerVertically>
        <View row centerVertically paddingVertical={1} paddingHorizontal={3}>
          <MBIconV2 name="list" size={22} color={colors.foregroundActive} />
          <View fill centerVertically paddingLeft={1}>
            <MBTextBody1 color={colors.foregroundActive}>
              {subformWorkflowTemplate?.name ?? ''}
            </MBTextBody1>
          </View>
          <MBTouchableOpacity
            onPress={() => {
              onDeletePress();
            }}
          >
            <MBIconV2 name="exit-small" size={22} />
          </MBTouchableOpacity>
        </View>
      </MBHover>
    </MBTouchableOpacity>
  );
}

interface AddSubformRowProps {
  onAdd(): void;
}

export function AddSubformRow({ onAdd }: AddSubformRowProps) {
  const colors = useContext(ColorThemeContext);

  return (
    <MBTouchableOpacity onPress={onAdd}>
      <MBHover hoverStyle={{ backgroundColor: colors.input.backgroundInactive }} centerVertically>
        <View row centerVertically paddingVertical={1} paddingHorizontal={3}>
          <MBIconV2 name="plus" size={22} color={colors.foregroundActive} />
          <View fill centerVertically paddingLeft={1}>
            <MBTextBody1 color={colors.foregroundActive}>
              {i18n('workflowTemplate.createNewSubform')}
            </MBTextBody1>
          </View>
        </View>
      </MBHover>
    </MBTouchableOpacity>
  );
}
